import {CdkTrapFocus} from '@angular/cdk/a11y';
import {
  afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, inject, Injector,
  input, output, signal, viewChild, ViewEncapsulation,
} from '@angular/core';
import {ChatServiceService} from '../_services/chatService.service';
import {ASSISTANT_NAME, STRINGS} from './assistant.constants';
import {ChatLogComponent} from './chat-log.component';
import {ChatStore} from './chat-store';
import {ComposerComponent} from './composer.component';
import {IconComponent} from './icon.component';
import {EmptyStateComponent} from './messages.components';

/** Below this width the panel is full screen (aria-modal, focus trap, page scroll locked). */
export const MOBILE_QUERY = '(max-width: 639.98px)';

@Component({
  selector: 'app-aa-chat-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: {'class': 'aa-header'},
  template: `
    <img class="aa-seal" src="assets/imgs/seal.png" alt="" width="28" height="28">
    <h2 [id]="titleId()">{{ name }}</h2>
    <div class="aa-header-actions">
      <button class="aa-icon-btn" type="button" [attr.aria-label]="strings.newChat" [title]="strings.newChat"
              [disabled]="!canStartNew()" (click)="newChat.emit()">
        <app-aa-icon name="pen"/>
      </button>
      @if (canExpand()) {
        <button class="aa-icon-btn" type="button" [attr.aria-label]="strings.expand" [title]="expanded() ? strings.restore : strings.expand"
                [attr.aria-pressed]="expanded()" (click)="toggleExpand.emit()">
          <app-aa-icon [name]="expanded() ? 'minimize' : 'maximize'"/>
        </button>
      }
      <button class="aa-icon-btn" type="button" [attr.aria-label]="strings.close" [title]="strings.close" (click)="close.emit()">
        <app-aa-icon name="x"/>
      </button>
    </div>
  `,
})
export class ChatHeaderComponent {
  readonly titleId = input.required<string>();
  readonly expanded = input(false);
  readonly canExpand = input(true);
  readonly canStartNew = input(false);

  readonly newChat = output<void>();
  readonly toggleExpand = output<void>();
  readonly close = output<void>();

  protected readonly name = ASSISTANT_NAME;
  protected readonly strings = STRINGS;
}

/**
 * The Assessor Assistant widget: launcher plus panel. Shown instead of the legacy chatbot when the
 * page URL has ?useNewChatbot. Opens from the launcher or from anything that calls
 * ChatServiceService.openChatBot() (nav menu, home page).
 */
@Component({
  selector: 'app-assessor-assistant',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrl: './assessor-assistant.scss',
  providers: [ChatStore],
  imports: [CdkTrapFocus, ChatHeaderComponent, ChatLogComponent, ComposerComponent, EmptyStateComponent, IconComponent],
  host: {'class': 'aa-root'},
  template: `
    @if (!chat.isOpen()) {
      <button #launcher class="aa-launcher" type="button" aria-haspopup="dialog" aria-expanded="false" (click)="open()">
        <app-aa-icon name="message"/>{{ strings.launcher }}
      </button>
    } @else {
      <div class="aa-panel"
           role="dialog"
           [attr.aria-modal]="mobile() ? 'true' : null"
           [attr.aria-labelledby]="titleId"
           [class.aa-expanded]="expanded() && !mobile()"
           [class.aa-mobile]="mobile()"
           [cdkTrapFocus]="mobile()"
           (keydown.escape)="close()">
        <app-aa-chat-header
          [titleId]="titleId"
          [expanded]="expanded()"
          [canExpand]="!mobile()"
          [canStartNew]="!store.isEmpty()"
          (newChat)="newChat()"
          (toggleExpand)="expanded.set(!expanded())"
          (close)="close()"/>
        @if (store.isEmpty()) {
          <app-aa-empty-state (pick)="store.send($event)"/>
        } @else {
          <app-aa-chat-log/>
        }
        <app-aa-composer #composer [busy]="store.busy()" (send)="store.send($event)" (stop)="store.stop()"/>
      </div>
    }
  `,
})
export class ChatPanelComponent {
  protected readonly chat = inject(ChatServiceService);
  protected readonly store = inject(ChatStore);
  protected readonly strings = STRINGS;
  protected readonly titleId = 'aa-title';
  protected readonly expanded = signal(false);
  protected readonly mobile = signal(false);

  private readonly composer = viewChild<ComposerComponent>('composer');
  private readonly launcher = viewChild<ElementRef<HTMLButtonElement>>('launcher');
  private readonly injector = inject(Injector);

  constructor() {
    const media = window.matchMedia?.(MOBILE_QUERY);
    if (media) {
      this.mobile.set(media.matches);
      const onChange = (event: MediaQueryListEvent) => this.mobile.set(event.matches);
      media.addEventListener('change', onChange);
      inject(DestroyRef).onDestroy(() => media.removeEventListener('change', onChange));
    }

    // Focus the composer whenever the panel opens (launcher, nav menu, or home page).
    let wasOpen = false;
    effect(() => {
      const isOpen = this.chat.isOpen();
      if (isOpen && !wasOpen) {
        afterNextRender(() => this.composer()?.focus(), {injector: this.injector});
      }
      wasOpen = isOpen;
    });

    // Full screen on small screens: the page behind must not scroll.
    effect(onCleanup => {
      if (this.chat.isOpen() && this.mobile()) {
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        onCleanup(() => (document.body.style.overflow = previous));
      }
    });
  }

  protected open(): void {
    this.chat.openChatBot();
  }

  protected close(): void {
    // Closing stops a running answer, so nobody pays for tokens nobody reads.
    this.store.stop();
    this.chat.closeChatBot();
    // If whatever opened the chat is gone or hidden (collapsed nav menu), focus the launcher.
    setTimeout(() => {
      const active = document.activeElement;
      if (!active || active === document.body) {
        this.launcher()?.nativeElement.focus();
      }
    }, 0);
  }

  protected newChat(): void {
    this.store.newChat();
    afterNextRender(() => this.composer()?.focus(), {injector: this.injector});
  }
}
